// ============================================================
// LIGHTING - how the scene is lit (plan map-display, F; the user's ruling:
// "lighting is set on the scene: daylight / lit areas / darkness").
//
// On the terrain, so a map and a scene carry their own. Daylight is the
// default and is ABSENT, never stored, so no older map changes. Under "lit
// areas", ground is lit where it is painted with a surface marked lit (a
// lamp's pool, a burning brazier's ring) and dark everywhere else. What reads
// it is Stealth on the display (Track H): the opposition's wedge reaches
// 6 m to a target in the dark and 11 m to one in the light. Senses come from
// the sheet, never from here.
//
// Pure. Zero React dependency.
// ============================================================
import type { Terrain } from './presets';
import { tileOf } from './boundary';
import { zoneCellKey, type Zone } from './zones';

/** The lit cells of a zone list, kept per list: Stealth asks for every token, every frame it moves. */
const litCache = new WeakMap<readonly Zone[], ReadonlySet<string>>();

function litCells(zones: readonly Zone[]): ReadonlySet<string> {
  let lit = litCache.get(zones);
  if (!lit) {
    lit = new Set(zones.filter((zone) => zone.light).flatMap((zone) => zone.cells ?? []));
    litCache.set(zones, lit);
  }
  return lit;
}

export type Lighting = 'daylight' | 'lit-areas' | 'darkness';

/** The scene's lighting; a map that says nothing is in daylight. */
export function lightingOf(terrain: Pick<Terrain, 'lighting'>): Lighting {
  return terrain.lighting ?? 'daylight';
}

/** The terrain lit this way: daylight takes the field off, so it is never stored. */
export function withLighting<T extends Pick<Terrain, 'lighting'>>(terrain: T, lighting: Lighting): T {
  const { lighting: _old, ...rest } = terrain;
  return (lighting === 'daylight' ? rest : { ...rest, lighting }) as T;
}

/** Whether the ground at (x, z), in metres, is lit or dark. */
export function lightAt(
  terrain: Pick<Terrain, 'lighting' | 'zones' | 'boundary'>,
  x: number,
  z: number,
): 'lit' | 'dark' {
  const lighting = lightingOf(terrain);
  if (lighting !== 'lit-areas') return lighting === 'darkness' ? 'dark' : 'lit';
  const tile = tileOf(terrain.boundary);
  const key = zoneCellKey(Math.floor(x / tile), Math.floor(z / tile));
  return terrain.zones && litCells(terrain.zones).has(key) ? 'lit' : 'dark';
}
